import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TrafficChart from '../components/Dashboard/TrafficChart';
import TopZipCodes from '../components/Dashboard/TopZipCodes';
import { analyticsAPI } from '../services/api';
import './Analytics.css';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [period, setPeriod] = useState('last7days');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [compareWithPrevious, setCompareWithPrevious] = useState(false);
  const navigate = useNavigate();

  const periods = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'monthToDate', label: 'Month to Date' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        period: period === 'custom' ? 'custom' : period,
        compareWithPrevious: compareWithPrevious.toString()
      };

      if (period === 'custom' && startDate && endDate) {
        params.startDate = startDate.toISOString().split('T')[0];
        params.endDate = endDate.toISOString().split('T')[0];
      }

      // console.log('Fetching analytics with params:', params);

      const response = await analyticsAPI.getTraffic(params);
      
      if (response.data.success) {
        setAnalyticsData(response.data.data);
      } else {
        setError('Failed to fetch analytics data');
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.message || 'Error fetching analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummaryData = async () => {
    try {
      const response = await analyticsAPI.getSummary();
      if (response.data.success) {
        setSummaryData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
    fetchSummaryData();
  }, []);

  const handlePeriodChange = (event) => {
    const newPeriod = event.target.value;
    setPeriod(newPeriod);
    
    if (newPeriod !== 'custom') {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const handleApplyFilter = () => {
    if (period === 'custom' && (!startDate || !endDate)) {
      setError('Please select both start and end dates for custom range');
      return;
    }
    
    if (period === 'custom' && startDate > endDate) {
      setError('Start date cannot be after end date');
      return;
    }

    fetchAnalyticsData();
  };

   
  const handleViewBookkeepers = (zipCode) => {
    // console.log('View bookkeepers for zip code:', zipCode);
    navigate(`/bookkeepers?zipcode=${zipCode}`);
  };

  if (loading && !analyticsData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box className="analytics-page">
        <Typography variant="h4" gutterBottom>
          Analytics Dashboard
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        {summaryData && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Today's Page Views
                  </Typography>
                  <Typography variant="h5" component="div">
                    {summaryData.today.pageViews.toLocaleString()}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color={summaryData.comparison.pageViews >= 0 ? 'success.main' : 'error.main'}
                  >
                    {summaryData.comparison.pageViews >= 0 ? '↑' : '↓'} 
                    {Math.abs(summaryData.comparison.pageViews)}% from yesterday
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Today's Unique Visitors
                  </Typography>
                  <Typography variant="h5" component="div">
                    {summaryData.today.uniqueVisitors.toLocaleString()}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color={summaryData.comparison.uniqueVisitors >= 0 ? 'success.main' : 'error.main'}
                  >
                    {summaryData.comparison.uniqueVisitors >= 0 ? '↑' : '↓'} 
                    {Math.abs(summaryData.comparison.uniqueVisitors)}% from yesterday
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Today's Sessions
                  </Typography>
                  <Typography variant="h5" component="div">
                    {summaryData.today.sessions.toLocaleString()}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color={summaryData.comparison.sessions >= 0 ? 'success.main' : 'error.main'}
                  >
                    {summaryData.comparison.sessions >= 0 ? '↑' : '↓'} 
                    {Math.abs(summaryData.comparison.sessions)}% from yesterday
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Today's Searches
                  </Typography>
                  <Typography variant="h5" component="div">
                    {summaryData.todaySearches.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Time Period</InputLabel>
                <Select
                  value={period}
                  label="Time Period"
                  onChange={handlePeriodChange}
                >
                  {periods.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {period === 'custom' && (
              <>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Comparison</InputLabel>
                <Select
                  value={compareWithPrevious}
                  label="Comparison"
                  onChange={(e) => setCompareWithPrevious(e.target.value)}
                >
                  <MenuItem value={false}>No Comparison</MenuItem>
                  <MenuItem value={true}>Compare with Previous</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={1}>
              <Button
                variant="contained"
                onClick={handleApplyFilter}
                disabled={loading}
                fullWidth
              >
                {loading ? <CircularProgress size={24} /> : 'Apply'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Analytics Data */}
        {analyticsData && (
          <>
            {/* Totals Card */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Period Totals ({analyticsData.period})
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="h4" color="primary">
                      {analyticsData.totals.pageViews.toLocaleString()}
                    </Typography>
                    <Typography color="textSecondary">
                      Page Views
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="h4" color="primary">
                      {analyticsData.totals.uniqueVisitors.toLocaleString()}
                    </Typography>
                    <Typography color="textSecondary">
                      Unique Visitors
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="h4" color="primary">
                      {analyticsData.totals.sessions.toLocaleString()}
                    </Typography>
                    <Typography color="textSecondary">
                      Sessions
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Traffic Chart */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Traffic Overview
                </Typography>
                <TrafficChart 
                  data={analyticsData.analytics}
                  period={analyticsData.period}
                  compareWithPrevious={analyticsData.comparison}
                />
              </CardContent>
            </Card>

            {/* Top Zip Codes */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Search Analytics
                </Typography>
                <TopZipCodes 
                  data={analyticsData.topZipCodes}
                  period={analyticsData.period}
                  onViewBookkeepers={handleViewBookkeepers}
                />
              </CardContent>
            </Card>
          </>
        )}

        {!loading && (!analyticsData || analyticsData.analytics.length === 0) && (
          <Alert severity="info">
            No analytics data available for the selected period.
          </Alert>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default Analytics;